import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Save, Loader2, Search, ChevronLeft, ChevronRight, CalendarDays, X, MessageSquare } from "lucide-react";
import { useTalmidim } from "../context/TalmidimContext";
import { useAbsences } from "../context/AbsencesContext";
import { useSettings } from "../context/SettingsContext";
import { commonReasons as defaultReasons } from "../mock";
import { useToast } from "../hooks/use-toast";
import { formatHebrewDateShort } from "../lib/hebrewDate";
import { groupReasons, CATEGORY_JUSTIFIED, CATEGORY_UNJUSTIFIED, CATEGORY_LABEL_HE, normalizeReasons } from "../lib/reasons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Day of week labels in Yiddish/Hebrew — Sunday first
const DOW_LABELS = [
  { short: "יום א'", en: "Sun" },
  { short: "יום ב'", en: "Mon" },
  { short: "יום ג'", en: "Tue" },
  { short: "יום ד'", en: "Wed" },
  { short: "יום ה'", en: "Thu" },
  { short: "יום ו'", en: "Fri" },
];

function toISO(d) {
  return d.toISOString().slice(0, 10);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
// Sunday of the week containing this date (locale-agnostic)
function sundayOf(d) {
  const day = d.getDay(); // 0 = Sun
  return addDays(d, -day);
}

export default function Attendance() {
  const { talmidim, klasn } = useTalmidim();
  const { refresh } = useAbsences();
  const { settings } = useSettings();
  const { toast } = useToast();

  const commonReasons = settings?.commonReasons?.length ? settings.commonReasons : defaultReasons;

  const [klasId, setKlasId] = useState("");
  const [weekStart, setWeekStart] = useState(sundayOf(new Date())); // Date obj for Sunday
  const [search, setSearch] = useState("");
  // absentMap: { [dateISO]: { [talmidId]: reason (string) } } — presence of key means absent
  const [absentMap, setAbsentMap] = useState({});
  const [dirtyDates, setDirtyDates] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!klasId && klasn.length > 0) setKlasId(String(klasn[0].id));
  }, [klasn, klasId]);

  const weekDates = useMemo(() => {
    // 6 days Sunday..Friday
    return Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const weekDateStrs = useMemo(() => weekDates.map(toISO), [weekDates]);

  // Load existing absences for this class over the 6 days
  useEffect(() => {
    if (!klasId || weekDateStrs.length === 0) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/absences`, {
          params: {
            klasId: Number(klasId),
            dateFrom: weekDateStrs[0],
            dateTo: weekDateStrs[weekDateStrs.length - 1],
          },
        });
        if (cancelled) return;
        const map = {};
        for (const d of weekDateStrs) map[d] = {};
        for (const a of res.data || []) {
          if (!map[a.date]) map[a.date] = {};
          map[a.date][a.talmidId] = a.reason || "";
        }
        setAbsentMap(map);
        setDirtyDates(new Set());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [klasId, weekDateStrs]);

  const classStudents = useMemo(() => {
    if (!klasId) return [];
    let s = talmidim.filter((t) => String(t.klasId) === String(klasId));
    if (search.trim()) s = s.filter((t) => t.fullName.includes(search));
    // Sort A→Z by last name (last whitespace-separated token in fullName)
    const lastName = (name) => {
      const parts = (name || "").trim().split(/\s+/);
      return parts[parts.length - 1] || "";
    };
    s = [...s].sort((a, b) =>
      lastName(a.fullName).localeCompare(lastName(b.fullName), "he", { sensitivity: "base" })
    );
    return s;
  }, [talmidim, klasId, search]);

  const toggle = useCallback((dateStr, tid) => {
    setAbsentMap((prev) => {
      const next = { ...prev };
      const day = { ...(next[dateStr] || {}) };
      if (Object.prototype.hasOwnProperty.call(day, tid)) {
        delete day[tid];
      } else {
        day[tid] = "";
      }
      next[dateStr] = day;
      return next;
    });
    setDirtyDates((prev) => new Set(prev).add(dateStr));
  }, []);

  const setReason = useCallback((dateStr, tid, reason) => {
    setAbsentMap((prev) => {
      const next = { ...prev };
      const day = { ...(next[dateStr] || {}) };
      day[tid] = reason;
      next[dateStr] = day;
      return next;
    });
    setDirtyDates((prev) => new Set(prev).add(dateStr));
  }, []);

  const save = async () => {
    if (!klasId || dirtyDates.size === 0) return;
    setSaving(true);
    try {
      const dates = Array.from(dirtyDates);
      for (const d of dates) {
        const day = absentMap[d] || {};
        const items = Object.entries(day).map(([talmidId, reason]) => ({
          talmidId: Number(talmidId),
          reason: reason || null,
        }));
        await axios.post(`${API}/absences/bulk`, {
          klasId: Number(klasId),
          date: d,
          absences: items,
        });
      }
      await refresh();
      setDirtyDates(new Set());
      toast({
        title: "Attendance saved",
        description: `Updated ${dates.length} day(s)`,
      });
    } catch (e) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Save failed",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const dirty = dirtyDates.size > 0;
  const selectedKlas = klasn.find((k) => String(k.id) === String(klasId));

  const weekLabel = useMemo(() => {
    const first = weekDates[0];
    const last = weekDates[weekDates.length - 1];
    const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(first)} – ${fmt(last)}, ${last.getFullYear()}`;
  }, [weekDates]);

  const today = toISO(new Date());

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="attendance-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-heb text-3xl font-bold text-slate-900">
            Attendance <span dir="rtl" className="text-slate-500 text-2xl">· חסרים</span>
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Weekly roll-call — check the box for any day a student was absent
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving || !dirty || !klasId}
          className="gradient-blue-mild text-white"
          data-testid="save-attendance-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {dirty ? `Save (${dirtyDates.size} day${dirtyDates.size === 1 ? "" : "s"})` : "Saved"}
        </Button>
      </div>

      {/* Controls */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-600 block mb-1">Class</label>
            <Select value={klasId} onValueChange={setKlasId}>
              <SelectTrigger data-testid="klas-select">
                <SelectValue placeholder="Select a class..." />
              </SelectTrigger>
              <SelectContent>
                {klasn.map((k) => (
                  <SelectItem key={k.id} value={String(k.id)}>
                    <span dir="rtl">{k.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-[280px]">
            <label className="text-xs font-medium text-slate-600 block mb-1">Week</label>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                onClick={() => setWeekStart(addDays(weekStart, -7))}
                title="Previous week"
                data-testid="prev-week-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-sm font-medium text-slate-700 font-en">
                <CalendarDays className="w-4 h-4 text-slate-400 mr-2" />
                {weekLabel}
              </div>
              <Button
                size="icon"
                variant="outline"
                onClick={() => setWeekStart(addDays(weekStart, 7))}
                title="Next week"
                data-testid="next-week-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setWeekStart(sundayOf(new Date()))}
                data-testid="this-week-btn"
              >
                This week
              </Button>
            </div>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-600 block mb-1">Filter students</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name"
                dir="rtl"
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weekly grid */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="text-sm text-slate-600">
              {selectedKlas ? (
                <>
                  <span dir="rtl" className="font-medium">{selectedKlas.name}</span>
                  <span className="text-slate-400 mx-1">·</span>
                  <span>{classStudents.length} students</span>
                </>
              ) : (
                "Pick a class above"
              )}
            </div>
            {loading && (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            )}
          </div>

          {classStudents.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">
              {klasId ? "No students in this class" : "Pick a class above to start"}
            </div>
          ) : (
            <div className="overflow-x-auto" dir="rtl">
              <table className="w-full border-collapse">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="w-10 px-2 py-3 text-center text-xs uppercase text-slate-500 border-b border-slate-200">
                      #
                    </th>
                    <th className="px-4 py-3 text-right text-xs uppercase text-slate-500 border-b border-slate-200 min-w-[200px]">
                      נאמען
                    </th>
                    {weekDates.map((d, i) => {
                      const iso = weekDateStrs[i];
                      const isToday = iso === today;
                      const absentCount = Object.keys(absentMap[iso] || {}).length;
                      return (
                        <th
                          key={iso}
                          className={`px-2 py-3 text-center border-b border-slate-200 min-w-[95px] ${
                            isToday ? "bg-blue-50" : ""
                          }`}
                        >
                          <div className="font-semibold text-slate-700 text-sm">
                            {DOW_LABELS[i].short}
                          </div>
                          <div className="text-[10px] text-slate-400 font-en mt-0.5" dir="ltr">
                            {DOW_LABELS[i].en} · {d.getDate()}/{d.getMonth() + 1}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-1">
                            {absentCount > 0 ? `${absentCount} חסר` : "\u00A0"}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {classStudents.map((t, idx) => (
                    <tr
                      key={t.id}
                      className="border-b border-slate-100 hover:bg-slate-50/60"
                      data-testid={`student-row-${t.id}`}
                    >
                      <td className="px-2 py-2 text-xs text-slate-400 font-mono text-center">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="font-medium text-slate-800">{t.fullName}</div>
                        {t.fatherName && (
                          <div className="text-xs text-slate-500">בן {t.fatherName}</div>
                        )}
                      </td>
                      {weekDateStrs.map((iso, i) => {
                        const day = absentMap[iso] || {};
                        const isAbsent = Object.prototype.hasOwnProperty.call(day, t.id);
                        const reason = isAbsent ? day[t.id] : "";
                        const isToday = iso === today;
                        return (
                          <td
                            key={iso}
                            className={`px-2 py-2 text-center transition-colors ${
                              isToday ? "bg-blue-50/40" : ""
                            } ${isAbsent ? "bg-red-50/70" : ""}`}
                          >
                            <AbsenceCell
                              isAbsent={isAbsent}
                              reason={reason}
                              onToggle={() => toggle(iso, t.id)}
                              onReasonChange={(r) => setReason(iso, t.id, r)}
                              commonReasons={commonReasons}
                              studentName={t.fullName}
                              testId={`cell-${t.id}-${iso}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-slate-500 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-50/70 border border-red-200 rounded" />
          <span>Absent (חסר)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded" />
          <span>Today</span>
        </div>
      </div>

      {dirty && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-4 py-1.5 text-xs shadow-md z-40">
          Unsaved changes — click "Save"
        </div>
      )}
    </div>
  );
}

function AbsenceCell({ isAbsent, reason, onToggle, onReasonChange, commonReasons, studentName, testId }) {
  const [open, setOpen] = useState(false);
  const [customReason, setCustomReason] = useState(reason || "");

  useEffect(() => {
    setCustomReason(reason || "");
  }, [reason]);

  // Click on empty cell → mark absent AND open popover to pick reason
  const handleCellClick = () => {
    if (!isAbsent) {
      onToggle();
    }
    setOpen(true);
  };

  const applyCustom = () => {
    onReasonChange(customReason.trim());
    setOpen(false);
  };

  const clearAbsence = () => {
    onToggle();
    setOpen(false);
  };

  const pickReason = (r) => {
    onReasonChange(r);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleCellClick}
          className="flex flex-col items-center justify-center gap-1 mx-auto min-h-[36px] px-1 hover:opacity-80"
          data-testid={testId}
          aria-label={`${studentName} absent`}
        >
          <Checkbox
            checked={isAbsent}
            className="h-5 w-5 pointer-events-none"
            tabIndex={-1}
          />
          {isAbsent && reason && (
            <span
              className="text-[10px] text-red-700 max-w-[80px] truncate leading-tight"
              title={reason}
            >
              {reason}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72 p-3" dir="rtl">
        <div className="text-xs font-semibold text-slate-600 mb-2">
          <span>סיבה פאר '{studentName}' זיין נישט דא</span>
        </div>
        {(() => {
          const groups = groupReasons(commonReasons);
          return [CATEGORY_JUSTIFIED, CATEGORY_UNJUSTIFIED].map((cat) => {
            const items = groups[cat];
            if (!items.length) return null;
            const isJustified = cat === CATEGORY_JUSTIFIED;
            return (
              <div key={cat} className="mb-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className={`w-1.5 h-1.5 rounded-full ${isJustified ? "bg-emerald-500" : "bg-red-500"}`} />
                  <div className="text-[10px] font-medium text-slate-600">
                    {CATEGORY_LABEL_HE[cat]}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {items.map((r) => (
                    <button
                      key={r.text}
                      type="button"
                      onClick={() => pickReason(r.text)}
                      className={`text-xs border rounded-full px-2.5 py-1 transition-colors ${
                        reason === r.text
                          ? isJustified
                            ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                            : "bg-red-100 border-red-300 text-red-800"
                          : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                      }`}
                      data-testid={`reason-quick-${r.text}`}
                    >
                      {r.text}
                    </button>
                  ))}
                </div>
              </div>
            );
          });
        })()}
        <div className="flex gap-1 mb-2">
          <Input
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); applyCustom(); }
            }}
            placeholder="אנדערע סיבה..."
            className="text-sm h-8"
            dir="rtl"
            data-testid="custom-reason-input"
          />
          <Button
            size="sm"
            className="h-8 px-2"
            onClick={applyCustom}
            disabled={customReason.trim() === (reason || "")}
            data-testid="apply-reason-btn"
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={clearAbsence}
            className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1"
            data-testid="clear-absence-btn"
          >
            <X className="w-3 h-3" />
            <span>לעש חסרון</span>
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs text-slate-500 hover:text-slate-800"
          >
            סגור
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
